import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import {
  type DragEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "../components/ui/button";
import { ApiError, apiGet } from "../lib/api";
import { cn } from "../lib/utils";
import { CarteOrdre } from "./cadres-ordre";
import { Perspective, useMouvementReduit } from "./cadres-perspective";
import { CartePlaques, PlaqueAgrandie } from "./cadres-plaques";
import { CarteReglages, lireSaisie, type SaisieCadre, saisieDepuis } from "./cadres-reglages";
import { type Apercu, blobDe, type EchecFichiers, useApercu, useFichiers } from "./cadres-rendu";
import {
  avertissements,
  decrireImage,
  estInterrompu,
  estOrdreInvalide,
  nomDuCadre,
  noteFichiers,
  pucesApercu,
  refusEnClair,
  TROP_DETAILLEE,
} from "./cadres-textes";
import { ALERTE, CARTE, EnTete, FOCUS, LongOuCourt, NOTE } from "./cadres-ui";
import {
  type ApercuReponse,
  type CadresInfo,
  cadresInfoSchema,
  type FormatCadre,
} from "./contract";
import { ConsoleShell } from "./shell";
import { ErreurImage, type ImageReduite, reduireImage } from "./silhouettes-image";
import type { ImageEnvoi } from "./silhouettes-rendu";
import { nettoyerNomFichier } from "./silhouettes-textes";
import { Chargement, EcranDErreur } from "./states";

/**
 * Le module Cadres : un cadre parallax a partir d'une image en aplats (spec du 24 septembre
 * 2026, issue #167, maquettes `docs/maquettes/console-lab/Cadres*.dc.html`). Deposer l'image,
 * reordonner ses couleurs, voir l'empilement en perspective et chaque plaque une par une,
 * telecharger le 3MF a un plateau par piece.
 *
 * **Rien n'est garde**, ni ici ni sur fortress : l'image reste dans la page, les fichiers
 * arrivent dans la reponse et deviennent des liens locaux (`Blob`).
 *
 * Mise en page : le DOM suit l'ordre du telephone (`CadresMobile.dc.html`) et du travail :
 * Image, Apercu, Plaques, Ordre, Fichiers, Reglages ; c'est lui que suivent le clavier et le
 * lecteur d'ecran. `grid-template-areas` place les cartes sans `order` :
 *
 * - sous `lg`, une colonne ;
 * - de `lg` a 1 359 px, deux colonnes : a gauche l'image, l'ordre, les fichiers et les
 *   reglages sur 330 px, a droite l'apercu et les plaques (trois colonnes laisseraient moins
 *   de 100 px a l'apercu a 1 024 px, le menu de la coquille prenant deja 13rem) ;
 * - a partir de 1 360 px, les trois colonnes de la maquette : 330 px, l'apercu, 350 px.
 */

function useCadresInfo(): UseQueryResult<CadresInfo, Error> {
  return useQuery({
    queryKey: ["cadres"],
    queryFn: () => apiGet("/api/cadres", cadresInfoSchema),
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function CadresPage() {
  const infos = useCadresInfo();
  // Une session perdue en cours de route (401, 403 sur un calcul) remplace la page.
  const [refus, setRefus] = useState<ApiError | null>(null);

  return (
    <ConsoleShell connecte={infos.isSuccess && refus === null}>
      {infos.isPending ? <Chargement quoi="des cadres" /> : null}
      {infos.isError ? <EcranDErreur error={infos.error} quoi="les cadres" /> : null}
      {refus ? <EcranDErreur error={refus} quoi="les cadres" /> : null}
      {infos.data && !refus ? <Atelier infos={infos.data} surRefus={setRefus} /> : null}
    </ConsoleShell>
  );
}

type ImageChoisie = ImageReduite & { nomFichier: string; fichier: File };

/** La fleche de la maquette, en caractere de texte (sans U+FE0E, Windows dessine un emoji). */
const FLECHE_BAS = String.fromCodePoint(0x2b07, 0xfe0e);

function Atelier({ infos, surRefus }: { infos: CadresInfo; surRefus: (erreur: ApiError) => void }) {
  const [image, setImage] = useState<ImageChoisie | null>(null);
  const [erreurImage, setErreurImage] = useState<string | null>(null);
  const [preparation, setPreparation] = useState(false);
  const [saisie, setSaisie] = useState<SaisieCadre>(() => saisieDepuis(infos.reglages));
  const [ordre, setOrdre] = useState<string[] | null>(null);
  const [survolee, setSurvolee] = useState<number | null>(null);
  const [ouverte, setOuverte] = useState<number | null>(null);
  const lecture = useMemo(() => lireSaisie(saisie), [saisie]);
  const envoi = useMemo<ImageEnvoi | null>(
    () =>
      image ? { base64: image.base64, nomFichier: nettoyerNomFichier(image.nomFichier) } : null,
    [image],
  );
  const rendu = useApercu(envoi, lecture.reglages, ordre);
  const fichiers = useFichiers(rendu.requete);
  const changerImage = useRef<HTMLButtonElement>(null);
  const dernierChoix = useRef(0);

  const apercu = rendu.apercu;
  // Montre pendant le calcul suivant (grise), remplace par une erreur.
  const montre = apercu !== null && (rendu.etat === "pret" || rendu.etat === "calcul");
  const aJour = rendu.etat === "pret" && apercu !== null && !lecture.largeurInvalide;
  const fichiersPossibles = aJour && !lecture.nomInvalide && rendu.requete !== null;
  const nom = lecture.nomInvalide ? null : lecture.reglages.nom;
  const isolee =
    survolee !== null && apercu?.plaques.some((plaque) => plaque.rang === survolee)
      ? survolee
      : null;

  // L'ordre de la requete qui a produit l'apercu affiche : la liste le compare a l'ordre choisi
  // (`ordre`) pour montrer tout de suite un ordre qui attend son calcul.
  const demande = rendu.requete?.ordre ?? null;

  useEffect(() => {
    if (image) changerImage.current?.focus();
  }, [image]);

  useEffect(() => {
    const statut = rendu.erreur?.status;
    if (rendu.erreur && (statut === 401 || statut === 403)) surRefus(rendu.erreur);
  }, [rendu.erreur, surRefus]);

  useEffect(() => {
    const echec = fichiers.erreur;
    if (echec && (echec.status === 401 || echec.status === 403)) {
      surRefus(new ApiError(echec.code, echec.message, echec.status));
    }
  }, [fichiers.erreur, surRefus]);

  // Un ordre qui ne nomme plus la palette (spec, section 3) : la page repart de la proposition.
  useEffect(() => {
    if (rendu.erreur?.code === "rendu_refuse" && estOrdreInvalide(rendu.erreur.message)) {
      setOrdre(null);
    }
  }, [rendu.erreur]);

  // Plus d'apercu, plus de plaque agrandie. Un recalcul qui donne moins de plaques la ramene a
  // la derniere qui existe (relecture de #176) : bornee des le rendu, puis dans l'etat.
  const plaqueOuverte =
    ouverte !== null && apercu ? Math.min(ouverte, apercu.plaques.length) : null;
  useEffect(() => {
    if (!apercu) setOuverte(null);
    else if (ouverte !== null && ouverte > apercu.plaques.length) {
      setOuverte(apercu.plaques.length);
    }
  }, [apercu, ouverte]);

  const choisir = async (fichier: File): Promise<void> => {
    dernierChoix.current += 1;
    const choix = dernierChoix.current;
    setPreparation(true);
    try {
      const reduite = await reduireImage(fichier);
      if (choix !== dernierChoix.current) return;
      setErreurImage(null);
      // La nouvelle image, son nom et l'ordre propose partent ensemble : une seule demande.
      setImage({ ...reduite, nomFichier: fichier.name, fichier });
      setOrdre(null);
      setSaisie((actuelle) => ({ ...actuelle, nom: nomDuCadre(fichier.name) }));
    } catch (erreur) {
      if (choix !== dernierChoix.current) return;
      setErreurImage(
        erreur instanceof ErreurImage ? erreur.message : `${fichier.name} n'a pas pu être lue.`,
      );
    } finally {
      if (choix === dernierChoix.current) setPreparation(false);
    }
  };

  const changerSaisie = (suivante: SaisieCadre): void => {
    // Le nombre de couleurs et la fenetre refont la palette : l'ordre repart de la proposition.
    if (suivante.couleurs !== saisie.couleurs || suivante.fenetre !== saisie.fenetre) {
      setOrdre(null);
    }
    setSaisie(suivante);
  };

  const lancer = (format: FormatCadre): void => {
    if (!fichiersPossibles || !rendu.requete || nom === null) return;
    fichiers.lancer({ ...rendu.requete, nom }, format);
  };

  return (
    <>
      <div>
        <h1 className="text-[21px] font-semibold md:text-2xl">Cadres</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          <LongOuCourt
            court="Une image en aplats, un cadre à imprimer. Rien n'est gardé ici."
            long="Une image en aplats, un cadre en profondeur à imprimer : une plaque par couleur, le cadre, son couvercle et son dos. Rien n'est gardé ici : tes fichiers partent dans tes Téléchargements."
          />
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[330px_minmax(0,1fr)] lg:grid-rows-[auto_auto_1fr] lg:items-start lg:[grid-template-areas:'image_centre'_'droite_centre'_'reglages_centre'] min-[85rem]:grid-cols-[330px_minmax(0,1fr)_350px] min-[85rem]:grid-rows-[auto_1fr] min-[85rem]:[grid-template-areas:'image_centre_droite'_'reglages_centre_droite']">
        <CarteImage
          boutonChanger={changerImage}
          choisir={(fichier) => void choisir(fichier)}
          className="lg:[grid-area:image]"
          couleurs={apercu?.palette.length}
          erreur={erreurImage}
          image={image}
          preparation={preparation}
        />
        <div className="flex min-w-0 flex-col gap-5 lg:[grid-area:centre]">
          <CarteApercu
            isolee={isolee}
            largeurDemandee={rendu.requete?.largeur_mm}
            montre={montre}
            rendu={rendu}
          />
          {apercu ? (
            <div className={cn("transition-opacity", rendu.etat === "calcul" && "opacity-60")}>
              <CartePlaques apercu={apercu} onOuvrir={setOuverte} onSurvol={setSurvolee} />
            </div>
          ) : (
            <CarteEnAttente
              aparte="de l'avant vers le fond"
              texte="Chaque plaque apparaîtra ici, telle qu'elle sort du plateau, avec l'aperçu."
              titre="Les plaques, une par une"
            />
          )}
        </div>
        <div className="flex min-w-0 flex-col gap-5 lg:[grid-area:droite]">
          {apercu ? (
            <CarteOrdre apercu={apercu} choisi={ordre} demande={demande} onOrdre={setOrdre} />
          ) : (
            <CarteEnAttente
              aparte={<LongOuCourt court="avant → fond" long="de l'avant vers le fond" />}
              texte="L'ordre des couleurs apparaîtra ici avec l'aperçu."
              titre="Ordre des plaques"
            />
          )}
          <CarteFichiers
            apercu={apercu}
            enCours={fichiers.enCours}
            erreur={fichiers.erreur}
            lancer={lancer}
            nom={nom}
            possible={fichiersPossibles}
          />
        </div>
        <CarteReglages
          apercu={apercu}
          avecImage={image !== null}
          changer={changerSaisie}
          className="lg:[grid-area:reglages]"
          ratio={image ? image.hauteur / image.largeur : null}
          saisie={saisie}
        />
      </div>

      {plaqueOuverte !== null && apercu ? (
        <PlaqueAgrandie
          apercu={apercu}
          onChanger={setOuverte}
          onFermer={() => setOuverte(null)}
          rang={plaqueOuverte}
        />
      ) : null}
    </>
  );
}

/** Une carte encore vide : son titre, et ce qui y apparaitra (l'ordre et les plaques). */
function CarteEnAttente({
  titre,
  aparte,
  texte,
}: {
  titre: string;
  aparte: ReactNode;
  texte: string;
}) {
  const id = useId();
  return (
    <section aria-labelledby={`${id}-titre`} className={CARTE}>
      <EnTete aparte={aparte} id={`${id}-titre`} titre={titre} />
      <p className="text-[13px] text-muted-foreground">{texte}</p>
    </section>
  );
}

/** Vrai quand un glisser porte des fichiers (et non du texte, ni une couleur de la liste). */
function porteDesFichiers(event: { dataTransfer: DataTransfer | null }): boolean {
  return Array.from(event.dataTransfer?.types ?? []).includes("Files");
}

function CarteImage({
  image,
  erreur,
  couleurs,
  preparation,
  choisir,
  boutonChanger,
  className,
}: {
  image: ImageChoisie | null;
  erreur: string | null;
  /** Le nombre de couleurs de l'apercu de cette image, une fois fait. */
  couleurs: number | undefined;
  preparation: boolean;
  choisir: (fichier: File) => void;
  boutonChanger: RefObject<HTMLButtonElement | null>;
  className: string;
}) {
  const id = useId();
  const champ = useRef<HTMLInputElement>(null);
  const [survol, setSurvol] = useState(false);
  const vignette = useUrlLocale(image?.fichier ?? null);

  // Une image lachee a cote de la carte ouvrirait le fichier dans l'onglet, et la page serait
  // perdue avec ses reglages et son ordre : un glisser de fichiers est retenu partout ailleurs.
  useEffect(() => {
    const retenir = (event: globalThis.DragEvent): void => {
      if (!porteDesFichiers(event) || event.defaultPrevented) return;
      event.preventDefault();
      if (event.type === "dragover" && event.dataTransfer) event.dataTransfer.dropEffect = "none";
    };
    document.addEventListener("dragover", retenir);
    document.addEventListener("drop", retenir);
    return () => {
      document.removeEventListener("dragover", retenir);
      document.removeEventListener("drop", retenir);
    };
  }, []);

  const deposer = (event: DragEvent<HTMLElement>): void => {
    if (!porteDesFichiers(event)) return;
    event.preventDefault();
    setSurvol(false);
    const fichier = event.dataTransfer.files[0];
    if (fichier) choisir(fichier);
  };

  return (
    <section
      aria-labelledby={`${id}-titre`}
      className={cn(CARTE, className)}
      onDragLeave={() => setSurvol(false)}
      onDragOver={(event) => {
        if (!porteDesFichiers(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
        setSurvol(true);
      }}
      onDrop={deposer}
    >
      <h2 className="mb-3.5 text-[15px] font-semibold" id={`${id}-titre`}>
        Image
      </h2>
      {/* Le champ de fichier est cache : ce sont les boutons de la carte qui l'ouvrent. */}
      <input
        accept="image/png,image/jpeg,image/webp"
        aria-label="Choisir un fichier"
        className="hidden"
        onChange={(event) => {
          const fichier = event.target.files?.[0];
          // Vide, pour que choisir le meme fichier une seconde fois le relise.
          event.target.value = "";
          if (fichier) choisir(fichier);
        }}
        ref={champ}
        type="file"
      />

      {image ? (
        <div
          className={cn(
            "flex items-center gap-4 rounded-lg border-[1.5px] border-dashed p-3.5",
            survol ? "border-foreground bg-muted" : "border-input bg-muted/40",
          )}
        >
          {vignette ? (
            <img
              alt=""
              className="damier w-[72px] shrink-0 rounded-md md:w-24"
              height={image.hauteur}
              src={vignette}
              width={image.largeur}
            />
          ) : null}
          <div className="min-w-0">
            <p className="truncate font-medium">{image.nomFichier}</p>
            <p className="text-[13px] text-muted-foreground">
              {decrireImage(image.largeur, image.hauteur, image.octets, couleurs)}
            </p>
            <button
              className={cn(
                "mt-2 inline-flex h-8 items-center justify-center rounded-md border border-input bg-card px-2.5 text-[13px] font-medium text-foreground hover:bg-muted",
                FOCUS,
              )}
              onClick={() => champ.current?.click()}
              ref={boutonChanger}
              type="button"
            >
              Changer d'image
            </button>
          </div>
        </div>
      ) : (
        <button
          className={cn(
            "flex w-full flex-col items-center gap-1 rounded-lg border-[1.5px] border-dashed px-4 py-[26px] text-center hover:bg-muted",
            FOCUS,
            survol ? "border-foreground bg-muted" : "border-input bg-muted/40",
          )}
          onClick={() => champ.current?.click()}
          type="button"
        >
          <span className="font-medium">Dépose une image, ou clique pour la choisir</span>
          <span className="text-[13px] text-muted-foreground">
            PNG, JPEG ou WebP, 10 Mo au plus. Une image en aplats de couleur : un dessin, une
            illustration, pas une photo.
          </span>
        </button>
      )}

      {/* Toujours la, pour que le lecteur d'ecran annonce le debut de la preparation. */}
      <p
        className={cn("text-[13px] text-muted-foreground", preparation ? "mt-3.5" : "sr-only")}
        role="status"
      >
        {preparation ? "Préparation de l'image…" : ""}
      </p>
      {erreur && !preparation ? (
        <p className={cn(ALERTE.rouge, "mt-3.5")} role="alert">
          {erreur}
        </p>
      ) : null}
    </section>
  );
}

/** Le petit rond qui tourne, fige avec `prefers-reduced-motion`. */
function Roue() {
  return (
    <span
      aria-hidden="true"
      className="inline-block size-4 shrink-0 animate-spin rounded-full border-2 border-border border-t-foreground align-[-3px] motion-reduce:animate-none"
    />
  );
}

function CarteApercu({
  rendu,
  montre,
  isolee,
  largeurDemandee,
}: {
  rendu: Apercu;
  montre: boolean;
  isolee: number | null;
  /** La largeur de la requete de l'apercu affiche, jamais la largeur tapee (relecture de #176). */
  largeurDemandee: number | undefined;
}) {
  const id = useId();
  const titre = useRef<HTMLHeadingElement>(null);
  const reduit = useMouvementReduit();
  const { etat, apercu, erreur, long } = rendu;
  const calcul = etat === "calcul";

  // Reessayer disparait avec l'erreur qui le portait : le focus passe au titre de la carte.
  const relancerPuisTitre = (): void => {
    rendu.relancer();
    titre.current?.focus();
  };

  const aparte =
    isolee !== null && montre ? (
      `plaque ${isolee} survolée`
    ) : calcul ? (
      <>
        <Roue /> calcul…
      </>
    ) : etat === "pret" ? (
      <LongOuCourt
        court={reduit ? "à jour" : "glisse le doigt dessus"}
        long={reduit ? "à jour" : "à jour · bouge la souris dessus"}
      />
    ) : null;

  const pastilleDeCalcul = (
    <span
      aria-hidden="true"
      className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-[13.5px] shadow-sm"
    >
      <Roue /> Calcul de l'aperçu… 1 à 3 s
    </span>
  );

  return (
    <section aria-busy={calcul} aria-labelledby={`${id}-titre`} className={CARTE}>
      <EnTete
        aparte={<span role="status">{aparte}</span>}
        id={`${id}-titre`}
        titre="Aperçu"
        titreRef={titre}
      />

      {calcul && long ? (
        <p className={cn(ALERTE.grise, "mb-3.5")}>
          Plus long que d'habitude : l'image est détaillée, ou un autre calcul passe avant. Le tien
          suit, sans rien à faire.
        </p>
      ) : null}

      {etat === "erreur" && erreur ? (
        <ErreurDeLApercu erreur={erreur} relancer={relancerPuisTitre} />
      ) : null}

      {montre && apercu ? (
        <div className="flex flex-col gap-3.5">
          <div className="relative">
            <div
              className={cn("transition-[opacity,filter]", calcul && "opacity-45 grayscale-[0.4]")}
            >
              <Perspective apercu={apercu} isolee={isolee} />
            </div>
            {calcul ? (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                {pastilleDeCalcul}
              </div>
            ) : null}
          </div>
          <ul aria-label="Chiffres du cadre" className="flex flex-wrap gap-2">
            {pucesApercu(apercu).map((puce) => (
              <li
                className={cn(
                  "rounded-full bg-muted px-2.5 py-1 text-[13.5px]",
                  puce.court === null && "hidden md:block",
                )}
                key={puce.long}
              >
                {puce.court === null ? (
                  puce.long
                ) : (
                  <LongOuCourt court={puce.court} long={puce.long} />
                )}
              </li>
            ))}
          </ul>
          <Avertissements apercu={apercu} largeurDemandee={largeurDemandee} />
          <p className={cn(NOTE, "hidden md:block")}>
            Ordre proposé : ce qui est bas dans l'image passe devant, puis le moins de plaques
            possible. Rien ne flotte : chaque plaque contient la précédente. Les plaques s'impriment
            face visible contre le plateau.
          </p>
        </div>
      ) : etat === "erreur" ? null : (
        <div className="flex min-h-40 flex-1 items-center justify-center rounded-lg border border-dashed border-border px-6 py-10 text-center text-sm text-muted-foreground">
          {etat === "vide"
            ? "L'aperçu apparaît ici dès qu'une image est déposée."
            : calcul
              ? pastilleDeCalcul
              : null}
        </div>
      )}
    </section>
  );
}

function Avertissements({
  apercu,
  largeurDemandee,
}: {
  apercu: ApercuReponse;
  /** La largeur de la requete de l'apercu affiche, jamais la largeur tapee (relecture de #176). */
  largeurDemandee: number | undefined;
}) {
  const liste = avertissements(apercu, largeurDemandee);
  if (liste.length === 0) return null;
  return (
    <ul aria-label="Avertissements de l'outil" className="flex flex-col gap-2">
      {liste.map((avertissement) => (
        <li className={ALERTE[avertissement.ton]} key={avertissement.texte}>
          {avertissement.texte}
        </li>
      ))}
    </ul>
  );
}

type Explication = { ton: "rouge" | "grise"; texte: string; reessayer: boolean };

/**
 * Ce que la page dit d'un echec du calcul, apercu ou fichiers (spec, section 5, et maquette
 * `CadresEtats.dc.html` ; memes messages que Silhouettes pour 413, 429, 502, 503 et 504). Un 400
 * qui n'est pas `rendu_refuse` vient de la validation de la console : son message (technique,
 * en anglais) n'est jamais montre.
 */
function expliquer(erreur: Pick<ApiError, "code" | "status" | "message">): Explication {
  if (erreur.status === 400 && erreur.code === "rendu_refuse") {
    if (estInterrompu(erreur.message)) {
      return { ton: "rouge", texte: TROP_DETAILLEE, reessayer: false };
    }
    if (estOrdreInvalide(erreur.message)) {
      return {
        ton: "grise",
        texte: "L'ordre ne correspondait plus aux couleurs de l'image : retour à l'ordre proposé.",
        reessayer: false,
      };
    }
    // Le conseil des aplats suit un refus de l'image, pas un cadre trop grand pour la P1S.
    return { ton: "rouge", texte: refusEnClair(erreur.message), reessayer: false };
  }
  if (erreur.status === 400) {
    return {
      ton: "rouge",
      texte: "Un réglage n'a pas été accepté : vérifie le nom et la largeur.",
      reessayer: false,
    };
  }
  if (erreur.status === 413) {
    return {
      ton: "rouge",
      texte:
        "L'image, même réduite, reste trop lourde pour l'envoi. Exporte-la en PNG plus petit, ou en JPEG.",
      reessayer: false,
    };
  }
  if (erreur.code === "calcul_en_panne") {
    return {
      ton: "grise",
      texte:
        "Le calcul a échoué sur cette image (erreur interne). Réessayer ne changera rien : essaie une autre image.",
      reessayer: false,
    };
  }
  const texte =
    erreur.status === 504
      ? "Le calcul ne répond pas (rien en 25 s)."
      : erreur.status === 429
        ? "Le calcul est occupé, réessaie."
        : erreur.code === "calcul_indisponible"
          ? "Le calcul des cadres n'est pas disponible sur le serveur pour l'instant."
          : "Le calcul est injoignable pour l'instant. Réessaie dans un moment.";
  return { ton: "grise", texte, reessayer: true };
}

/** Un encadre d'echec, et son bouton Reessayer quand reessayer peut changer quelque chose. */
function Echec({ explication, relancer }: { explication: Explication; relancer: () => void }) {
  if (!explication.reessayer) {
    return (
      <p className={ALERTE[explication.ton]} role="alert">
        {explication.texte}
      </p>
    );
  }
  return (
    <div
      className={cn(ALERTE[explication.ton], "flex items-center justify-between gap-3")}
      role="alert"
    >
      <span className="min-w-0 flex-1">{explication.texte}</span>
      <Button className="shrink-0" onClick={relancer} size="sm" variant="outline">
        Réessayer
      </Button>
    </div>
  );
}

function ErreurDeLApercu({ erreur, relancer }: { erreur: ApiError; relancer: () => void }) {
  const explication = expliquer(erreur);
  const deLOutil =
    erreur.code === "rendu_refuse" &&
    !estInterrompu(erreur.message) &&
    !estOrdreInvalide(erreur.message);
  return (
    <div className="mb-3.5 flex flex-col gap-2.5">
      <Echec explication={explication} relancer={relancer} />
      {deLOutil ? (
        <p className="text-[13px] text-muted-foreground">
          Le message vient de l'outil ; les fichiers restent désactivés.
        </p>
      ) : null}
    </div>
  );
}

const BOUTON_FICHIER =
  "inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border px-2.5 text-sm font-medium";

function CarteFichiers({
  apercu,
  nom,
  possible,
  enCours,
  erreur,
  lancer,
}: {
  apercu: ApercuReponse | null;
  /** Le nom des fichiers, `null` tant qu'il est invalide. */
  nom: string | null;
  /** Vrai quand l'apercu affiche repond aux reglages affiches, et que le nom est valide. */
  possible: boolean;
  enCours: FormatCadre | null;
  erreur: EchecFichiers | null;
  lancer: (format: FormatCadre) => void;
}) {
  const id = useId();
  const planche = useLienPlanche(apercu);

  // Une fabrication a la fois : pendant le 3MF, les STL attendent (et l'inverse).
  const bouton = (format: FormatCadre, principal: boolean, contenu: ReactNode): ReactNode => {
    const eteint = !possible || enCours !== null;
    return (
      <button
        aria-disabled={eteint || undefined}
        className={cn(
          BOUTON_FICHIER,
          FOCUS,
          principal ? "w-full" : "flex-1",
          eteint
            ? principal
              ? "cursor-default border-transparent bg-muted-foreground/30 text-muted-foreground"
              : "cursor-default border-border bg-muted/50 text-muted-foreground"
            : principal
              ? "border-primary bg-primary text-primary-foreground hover:opacity-90"
              : "border-input bg-card text-foreground hover:bg-muted",
        )}
        onClick={() => {
          if (!eteint) lancer(format);
        }}
        type="button"
      >
        {contenu}
      </button>
    );
  };

  return (
    <section aria-labelledby={`${id}-titre`} className={CARTE}>
      <h2 className="mb-3.5 hidden text-[15px] font-semibold md:block" id={`${id}-titre`}>
        Fichiers
      </h2>
      <div className="flex flex-col gap-2.5">
        {bouton(
          "3mf",
          true,
          enCours === "3mf" ? (
            <>
              <Roue /> Fabrication du 3MF… jusqu'à 15 s
            </>
          ) : (
            <>
              <span aria-hidden="true">{FLECHE_BAS}</span> Télécharger{" "}
              {nom ? `${nom}.3mf` : "le 3MF"}
            </>
          ),
        )}
        <div className="flex gap-2.5">
          {bouton(
            "stl",
            false,
            enCours === "stl" ? (
              <>
                <Roue /> Fabrication des STL…
              </>
            ) : (
              <LongOuCourt court="STL (zip)" long="Les STL (zip)" />
            ),
          )}
          <LienPlanche
            href={possible && nom ? planche : undefined}
            nom={nom ? `${nom}-planche.png` : undefined}
          >
            <LongOuCourt court="Planche" long="La planche (PNG)" />
          </LienPlanche>
        </div>
      </div>
      {erreur ? (
        <div className="mt-3.5">
          <Echec explication={expliquer(erreur)} relancer={() => lancer(erreur.format)} />
        </div>
      ) : null}
      {apercu ? (
        <p className={cn(NOTE, "mt-3.5 hidden md:block")}>
          {noteFichiers(apercu.plaques.length, apercu.rapport.led)}
        </p>
      ) : null}
    </section>
  );
}

/** Le lien de la planche, ou sa forme eteinte, hors de l'ordre de tabulation (comme les silhouettes). */
function LienPlanche({
  href,
  nom,
  children,
}: {
  href: string | undefined;
  nom: string | undefined;
  children: ReactNode;
}) {
  if (!href || !nom) {
    return (
      // biome-ignore lint/a11y/useValidAnchor: un lien eteint n'a volontairement pas de cible ; aria-disabled et role le disent, et sans href il sort de l'ordre de tabulation
      <a
        aria-disabled="true"
        className={cn(
          BOUTON_FICHIER,
          "flex-1 cursor-default border-border bg-muted/50 text-muted-foreground",
        )}
        role="link"
      >
        {children}
      </a>
    );
  }
  return (
    <a
      className={cn(
        BOUTON_FICHIER,
        FOCUS,
        "flex-1 border-input bg-card text-foreground hover:bg-muted",
      )}
      download={nom}
      href={href}
    >
      {children}
    </a>
  );
}

/**
 * Le lien local de la planche de l'apercu : cree a chaque nouvel apercu, revoque quand il est
 * remplace et au demontage, avant que l'ecran ne se peigne (`useLayoutEffect`).
 */
function useLienPlanche(apercu: ApercuReponse | null): string | undefined {
  const [lien, setLien] = useState<string | undefined>(undefined);
  useLayoutEffect(() => {
    if (!apercu) {
      setLien(undefined);
      return;
    }
    const cree = URL.createObjectURL(blobDe(apercu.planche_png, "image/png"));
    setLien(cree);
    return () => URL.revokeObjectURL(cree);
  }, [apercu]);
  return lien;
}

/** Un lien local vers le fichier choisi (la vignette), revoque au changement d'image. */
function useUrlLocale(fichier: File | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!fichier) {
      setUrl(null);
      return;
    }
    const lien = URL.createObjectURL(fichier);
    setUrl(lien);
    return () => URL.revokeObjectURL(lien);
  }, [fichier]);
  return url;
}
